import { createAsyncThunk } from "@reduxjs/toolkit";
import { ThunkApiConfig } from "@shared/models/redux";
import { UserIFC } from "@myProfile/models";

/**
 * Fetches user IFCs (Investor Fund Contacts) from the Snowflake data API.
 *
 * API Endpoint: GET /contacts/v1/system/SnowflakeData/ifc/{contactId}
 *
 * Response:
 * - Success: Array of UserIFC objects (user has IFCs)
 * - Error: { error: "No IFC data found for Contact ID '...'" } (user has NO IFCs)
 *
 * Used to determine profile menu visibility:
 * - Users with IFCs → can see My Profile
 * - Users without IFCs → cannot see My Profile or Manage Contacts
 */
export const fetchUserIFCsThunk = createAsyncThunk<UserIFC[], string, ThunkApiConfig>(
  "myProfile/fetchUserIFCs",
  async (sfdcContactId, { extra, rejectWithValue }) => {
    try {
      const url = `/contacts/v1/system/SnowflakeData/ifc/${sfdcContactId}`;

      const response = await extra<UserIFC[]>(url, {
        method: "GET",
      });

      return response.data;
    } catch (error: any) {
      // API returns error when no IFCs found - this is expected behavior
      // Check if it's the "No IFC data found" error
      const errorMessage = error?.response?.data?.error || error?.message || "Failed to fetch user IFCs";

      if (errorMessage.includes("No IFC data found")) {
        // Return empty array for "no IFCs" case - not a failure, just no data
        return [];
      }

      console.error("[fetchUserIFCs] Error fetching user IFCs:", error);

      return rejectWithValue(errorMessage);
    }
  }
);
